import { FormEvent, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import Badge from '../components/common/Badge';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { TableColumn } from '../components/common/MobileCardList';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';
import {
  BillingRateConfig,
  BillingType,
  CreateBillingConfigPayload,
} from '../services/billingConfigService';
import {
  useBillingConfigs,
  useBillingConfigHistory,
  useCreateBillingConfig,
} from '../hooks/useBillingConfigs';

const BILLING_TYPES: { value: BillingType; label: string }[] = [
  { value: 'per_pallet', label: 'Per Pallet' },
  { value: 'per_sqft', label: 'Per Square Foot' },
  { value: 'fixed_monthly', label: 'Fixed Monthly' },
];

interface FormState {
  billingType: BillingType;
  storageRatePerPallet: string;
  storageRatePerSqft: string;
  fixedMonthlyCharge: string;
  sqftAssigned: string;
  coldStorageRate: string;
  additionalStorageRate: string;
  additionalSpaceRatePerPallet: string;
  additionalSpaceRatePerSqft: string;
  handlingChargePerTon: string;
  hamaliChargePerTon: string;
  packingChargePerUnit: string;
  labelChargePerUnit: string;
  stickerChargePerUnit: string;
  effectiveFrom: string;
}

function emptyForm(billingType: BillingType): FormState {
  return {
    billingType,
    storageRatePerPallet: '',
    storageRatePerSqft: '',
    fixedMonthlyCharge: '',
    sqftAssigned: '',
    coldStorageRate: '',
    additionalStorageRate: '',
    additionalSpaceRatePerPallet: '',
    additionalSpaceRatePerSqft: '',
    handlingChargePerTon: '',
    hamaliChargePerTon: '',
    packingChargePerUnit: '',
    labelChargePerUnit: '',
    stickerChargePerUnit: '',
    effectiveFrom: '',
  };
}

function configToForm(config: BillingRateConfig): FormState {
  return {
    billingType: config.billingType,
    storageRatePerPallet: config.storageRatePerPallet != null ? String(config.storageRatePerPallet) : '',
    storageRatePerSqft: config.storageRatePerSqft != null ? String(config.storageRatePerSqft) : '',
    fixedMonthlyCharge: config.fixedMonthlyCharge != null ? String(config.fixedMonthlyCharge) : '',
    sqftAssigned: config.sqftAssigned != null ? String(config.sqftAssigned) : '',
    coldStorageRate: config.coldStorageRate != null ? String(config.coldStorageRate) : '',
    additionalStorageRate: config.additionalStorageRate != null ? String(config.additionalStorageRate) : '',
    additionalSpaceRatePerPallet: config.additionalSpaceRatePerPallet != null ? String(config.additionalSpaceRatePerPallet) : '',
    additionalSpaceRatePerSqft: config.additionalSpaceRatePerSqft != null ? String(config.additionalSpaceRatePerSqft) : '',
    handlingChargePerTon: config.handlingChargePerTon != null ? String(config.handlingChargePerTon) : '',
    hamaliChargePerTon: config.hamaliChargePerTon != null ? String(config.hamaliChargePerTon) : '',
    packingChargePerUnit: config.packingChargePerUnit != null ? String(config.packingChargePerUnit) : '',
    labelChargePerUnit: config.labelChargePerUnit != null ? String(config.labelChargePerUnit) : '',
    stickerChargePerUnit: config.stickerChargePerUnit != null ? String(config.stickerChargePerUnit) : '',
    effectiveFrom: config.effectiveFrom ?? '',
  };
}

// Collapses leading zeros as the user types (e.g. "00000100" -> "100"),
// while leaving a lone "0" or a decimal like "0.5" untouched.
function stripLeadingZeros(value: string): string {
  return value.replace(/^0+(?=\d)/, '');
}

function toNum(val: string): number | undefined {
  if (val === '' || val === null) return undefined;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

function fmtDate(dateStr: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmt(val: number | null | undefined): string {
  if (val == null) return '—';
  return `₹ ${val.toFixed(2)}`;
}

// Effective From is the only required field on this form — every rate field
// is optional (a config can legitimately leave charges unset/zero).
function requiredFieldsFor(_billingType: BillingType): Array<{ key: keyof FormState; label: string }> {
  return [{ key: 'effectiveFrom', label: 'Effective From' }];
}

export default function BillingConfigPage() {
  const { showToast } = useToast();

  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [editingType, setEditingType] = useState<BillingType | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm('per_pallet'));
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // The editor opens below the stacked type cards; bring it into view (matters on mobile)
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    if (editingType) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editingType]);

  // Fetch all clients for the selector
  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'list', { filters: { limit: 100 } }],
    queryFn: () => api.get('/clients?limit=100&page=1') as any,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  const clients: Array<{ id: number; companyName: string }> =
    (clientsData as any)?.data?.items ?? [];

  const clientOptions = [
    { value: '', label: 'Select a client...' },
    ...clients.map((c) => ({ value: c.id, label: c.companyName })),
  ];

  // Up to 3 concurrently active configs (one per billing type) + full history for this client
  const { data: activeConfigs, isLoading: loadingConfig } = useBillingConfigs(selectedClientId);
  const { data: history } = useBillingConfigHistory(selectedClientId);

  const createMutation = useCreateBillingConfig();
  const isSaving = createMutation.isPending;

  function activeConfigFor(billingType: BillingType): BillingRateConfig | undefined {
    return activeConfigs?.find((c) => c.billingType === billingType);
  }

  function historyFor(billingType: BillingType): BillingRateConfig[] {
    return (history ?? []).filter((h) => h.billingType === billingType);
  }

  function openEditor(billingType: BillingType) {
    const existing = activeConfigFor(billingType);
    setForm(existing ? configToForm(existing) : emptyForm(billingType));
    setFormErrors({});
    setEditingType(billingType);
  }

  function handleField(field: keyof FormState, value: string) {
    const sanitized = field === 'effectiveFrom' ? value : stripLeadingZeros(value);
    setForm((prev) => ({ ...prev, [field]: sanitized }));
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }

  // Walks the required fields top-to-bottom and stops at the first empty one,
  // so only one message shows at a time — matching the form's field order.
  function validateForm(): boolean {
    for (const f of requiredFieldsFor(form.billingType)) {
      if (!form[f.key].trim()) {
        setFormErrors({ [f.key]: `${f.label} is required` });
        return false;
      }
    }
    setFormErrors({});
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedClientId) return;
    if (!validateForm()) return;

    const payload: CreateBillingConfigPayload = {
      clientId: selectedClientId,
      billingType: form.billingType,
      storageRatePerPallet: toNum(form.storageRatePerPallet) ?? null,
      storageRatePerSqft: toNum(form.storageRatePerSqft) ?? null,
      fixedMonthlyCharge: toNum(form.fixedMonthlyCharge) ?? null,
      coldStorageRate: toNum(form.coldStorageRate) ?? null,
      additionalStorageRate: toNum(form.additionalStorageRate) ?? null,
      additionalSpaceRatePerPallet: toNum(form.additionalSpaceRatePerPallet) ?? null,
      additionalSpaceRatePerSqft: toNum(form.additionalSpaceRatePerSqft) ?? null,
      sqftAssigned: toNum(form.sqftAssigned) ?? null,
      handlingChargePerTon: toNum(form.handlingChargePerTon) ?? null,
      hamaliChargePerTon: toNum(form.hamaliChargePerTon) ?? null,
      packingChargePerUnit: toNum(form.packingChargePerUnit) ?? null,
      labelChargePerUnit: toNum(form.labelChargePerUnit) ?? null,
      stickerChargePerUnit: toNum(form.stickerChargePerUnit) ?? null,
      effectiveFrom: form.effectiveFrom,
    };

    try {
      await createMutation.mutateAsync(payload);
      showToast('Billing config saved successfully', 'success');
      setEditingType(null);
      setFormErrors({});
    } catch (err: any) {
      showToast(err?.message ?? 'Failed to save billing config', 'error');
    }
  }

  function historyColumns(billingType: BillingType): TableColumn[] {
    return [
      {
        key: 'effectiveFrom',
        label: 'Effective From',
        render: (_: any, row: BillingRateConfig) => fmtDate(row.effectiveFrom),
      },
      {
        key: 'effectiveTo',
        label: 'Effective To',
        render: (_: any, row: BillingRateConfig) => (row.effectiveTo ? fmtDate(row.effectiveTo) : '—'),
      },
      {
        key: 'rate',
        label: 'Primary Rate',
        render: (_: any, row: BillingRateConfig) => {
          if (billingType === 'per_pallet') return fmt(row.storageRatePerPallet);
          if (billingType === 'per_sqft') return fmt(row.storageRatePerSqft);
          return fmt(row.fixedMonthlyCharge);
        },
      },
      {
        key: 'handlingChargePerTon',
        label: 'Handling/Ton',
        render: (_: any, row: BillingRateConfig) => fmt(row.handlingChargePerTon),
      },
      {
        key: 'hamaliChargePerTon',
        label: 'Hamali/Ton',
        render: (_: any, row: BillingRateConfig) => fmt(row.hamaliChargePerTon),
      },
      {
        key: 'status',
        label: 'Status',
        render: (_: any, row: BillingRateConfig) =>
          row.isActive ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : (
            <Badge variant="secondary" size="sm">Expired</Badge>
          ),
      },
    ];
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-xl lg:text-2xl font-extrabold text-ink-900">Billing Configuration</h1>
          <p className="text-sm text-ink-500 mt-1">
            Set per-client billing rates for storage, handling, labour, and value-added services.
            A client can hold an active rate for each billing type at the same time.
          </p>
        </div>

        {/* Client Selector */}
        <div className="bg-white border border-ink-200 rounded-xl p-4 mb-4 lg:mb-6 overflow-visible relative z-20">
          <label className="block text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-2">Select Client</label>
          <ResponsiveSelect
            options={clientOptions.filter((o) => o.value !== '').map((o) => ({ value: o.value, label: o.label }))}
            value={selectedClientId ?? ''}
            onChange={(val) => {
              setSelectedClientId(val === '' ? null : Number(val));
              setEditingType(null);
              setFormErrors({});
            }}
            placeholder="Select a client..."
            includeEmpty
            ariaLabel="Select client"
            className="w-full border border-ink-200 rounded-xl px-3 py-2.5 text-sm min-h-[44px]"
          />
        </div>

        {selectedClientId == null && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-sm">
            Select a client above to view or configure billing rates.
          </div>
        )}

        {selectedClientId != null && loadingConfig && (
          <div className="text-center py-10 text-gray-400 text-sm">Loading config...</div>
        )}

        {selectedClientId != null && !loadingConfig && (
          <>
            {/* One card per billing type — each independently active/inactive */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {BILLING_TYPES.map(({ value, label }) => {
                const config = activeConfigFor(value);
                return (
                  <div key={value} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
                      {config ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          Not configured
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      {config ? (
                        <div className="space-y-1.5 mb-3">
                          {value === 'per_pallet' && (
                            <Row label="Rate per Pallet" value={fmt(config.storageRatePerPallet)} />
                          )}
                          {value === 'per_sqft' && (
                            <>
                              <Row label="Rate per Sqft" value={fmt(config.storageRatePerSqft)} />
                              <Row label="Sqft Assigned" value={config.sqftAssigned != null ? String(config.sqftAssigned) : '—'} />
                            </>
                          )}
                          {value === 'fixed_monthly' && (
                            <Row label="Fixed Monthly" value={fmt(config.fixedMonthlyCharge)} />
                          )}
                          <Row label="Active from" value={fmtDate(config.effectiveFrom)} />
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mb-3">No active rate for this billing type.</p>
                      )}
                      <button
                        type="button"
                        onClick={() => openEditor(value)}
                        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
                      >
                        {config ? 'Update Rate' : 'Add Rate'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Form — editing one specific billing type at a time */}
            {editingType && (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6 mb-6">
                <div className="bg-brand-50 border border-brand-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-brand-800">
                    {activeConfigFor(editingType) ? 'Updating' : 'Adding'} rate — {BILLING_TYPES.find((t) => t.value === editingType)?.label}
                  </span>
                  <button type="button" onClick={() => { setEditingType(null); setFormErrors({}); }} className="text-xs text-brand-600 hover:text-brand-800 underline">
                    Cancel
                  </button>
                </div>

                {/* Storage section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Storage</span>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {form.billingType === 'per_pallet' && (
                      <Input
                        label="Rate per Pallet / Month (₹)"
                        type="number"
                        step="0.01"
                        value={form.storageRatePerPallet}
                        onChange={(e) => handleField('storageRatePerPallet', e.target.value)}
                        error={formErrors.storageRatePerPallet}
                      />
                    )}
                    {form.billingType === 'per_sqft' && (
                      <>
                        <Input
                          label="Rate per Sqft / Month (₹)"
                          type="number"
                          step="0.01"
                          value={form.storageRatePerSqft}
                          onChange={(e) => handleField('storageRatePerSqft', e.target.value)}
                          error={formErrors.storageRatePerSqft}
                        />
                        <Input
                          label="Sqft Assigned"
                          type="number"
                          step="0.01"
                          value={form.sqftAssigned}
                          onChange={(e) => handleField('sqftAssigned', e.target.value)}
                          error={formErrors.sqftAssigned}
                        />
                      </>
                    )}
                    {form.billingType === 'fixed_monthly' && (
                      <Input
                        label="Fixed Monthly Charge (₹)"
                        type="number"
                        step="0.01"
                        value={form.fixedMonthlyCharge}
                        onChange={(e) => handleField('fixedMonthlyCharge', e.target.value)}
                        error={formErrors.fixedMonthlyCharge}
                      />
                    )}
                    <Input
                      label="Cold Storage Rate / Pallet (₹)"
                      type="number"
                      step="0.01"
                      value={form.coldStorageRate}
                      onChange={(e) => handleField('coldStorageRate', e.target.value)}
                      helperText="Optional — the only field on this form that isn't required"
                    />
                    <Input
                      label="Additional Storage Rate (₹)"
                      type="number"
                      step="0.01"
                      value={form.additionalStorageRate}
                      onChange={(e) => handleField('additionalStorageRate', e.target.value)}
                      helperText="Charged per pallet used beyond the client's allocated pallet/sqft limit (set on the client record)"
                      error={formErrors.additionalStorageRate}
                    />
                    {form.billingType === 'per_pallet' && (
                      <Input
                        label="Additional Space Rate / Pallet / Day (₹)"
                        type="number"
                        step="0.01"
                        value={form.additionalSpaceRatePerPallet}
                        onChange={(e) => handleField('additionalSpaceRatePerPallet', e.target.value)}
                        helperText="Charged against tracked additional-space overflow periods (Reports → Additional Space), prorated by day — separate from Additional Storage Rate above"
                        error={formErrors.additionalSpaceRatePerPallet}
                      />
                    )}
                    {form.billingType === 'per_sqft' && (
                      <Input
                        label="Additional Space Rate / Sqft / Day (₹)"
                        type="number"
                        step="0.01"
                        value={form.additionalSpaceRatePerSqft}
                        onChange={(e) => handleField('additionalSpaceRatePerSqft', e.target.value)}
                        helperText="Charged against tracked additional-space overflow periods (Reports → Additional Space), prorated by day — separate from Additional Storage Rate above"
                        error={formErrors.additionalSpaceRatePerSqft}
                      />
                    )}
                  </div>
                </div>

                {/* Handling & Labour section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Handling &amp; Labour</span>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Input
                      label="Manual Handling Charge / Ton (₹)"
                      type="number"
                      step="0.01"
                      value={form.handlingChargePerTon}
                      onChange={(e) => handleField('handlingChargePerTon', e.target.value)}
                      error={formErrors.handlingChargePerTon}
                    />
                    <Input
                      label="MHE Handling Charge / Ton (₹)"
                      type="number"
                      step="0.01"
                      value={form.hamaliChargePerTon}
                      onChange={(e) => handleField('hamaliChargePerTon', e.target.value)}
                      error={formErrors.hamaliChargePerTon}
                    />
                  </div>
                </div>

                {/* VAS section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Value Added Services</span>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Input
                      label="Packing Charge / Unit (₹)"
                      type="number"
                      step="0.01"
                      value={form.packingChargePerUnit}
                      onChange={(e) => handleField('packingChargePerUnit', e.target.value)}
                      error={formErrors.packingChargePerUnit}
                    />
                    <Input
                      label="Label Charge / Unit (₹)"
                      type="number"
                      step="0.01"
                      value={form.labelChargePerUnit}
                      onChange={(e) => handleField('labelChargePerUnit', e.target.value)}
                      error={formErrors.labelChargePerUnit}
                    />
                    <Input
                      label="Sticker Charge / Unit (₹)"
                      type="number"
                      step="0.01"
                      value={form.stickerChargePerUnit}
                      onChange={(e) => handleField('stickerChargePerUnit', e.target.value)}
                      error={formErrors.stickerChargePerUnit}
                    />
                  </div>
                </div>

                {/* Effective From + actions */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="w-56">
                      <Input
                        label="Effective From"
                        type="date"
                        required
                        value={form.effectiveFrom}
                        onChange={(e) => handleField('effectiveFrom', e.target.value)}
                        error={formErrors.effectiveFrom}
                      />
                    </div>
                    <div className="flex gap-3 mb-1">
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? 'Saving...' : 'Save Config'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingType(null); setFormErrors({}); }}
                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  {activeConfigFor(editingType) && (
                    <p className="text-xs text-amber-600 mt-2">
                      Saving will create a new config effective from the date above and expire the current {BILLING_TYPES.find((t) => t.value === editingType)?.label.toLowerCase()} rate.
                    </p>
                  )}
                </div>
              </form>
            )}

            {/* History — one grid per billing type (desktop table + mobile cards) */}
            {BILLING_TYPES.map(({ value, label }) => {
              const rows = historyFor(value);
              if (rows.length === 0) return null;
              return (
                <div key={value} className="bg-white border border-ink-200 rounded-xl overflow-hidden mt-4 lg:mt-6">
                  <div className="bg-ink-50 border-b border-ink-200 px-4 py-2.5">
                    <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider">
                      {label} — Rate History ({rows.length})
                    </span>
                  </div>
                  <div className="px-1 py-3 lg:p-0">
                    <ResponsiveTable
                      columns={historyColumns(value)}
                      data={rows}
                      emptyMessage="No history for this billing type"
                      height="auto"
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </Layout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}
